// Documents tab inside Case Detail. Lists docs in the Case, provides
// inline upload (multipart) + disassociation, and links each doc to
// its detail page.

import { useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, cases as casesApi, documents as docsApi } from "../../lib/api";
import { useAsync } from "../../hooks/useAsync";
import { pushToast } from "../Toast";
import { CrossCheckableBadge, DocumentStatusBadge } from "../StatusBadge";

interface Props {
  caseId: number;
  onChanged: () => void;
}

export function CaseDocumentsTab({ caseId, onChanged }: Props): JSX.Element {
  const { data, loading, error, refresh } = useAsync(
    () => casesApi.documents(caseId),
    [caseId],
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const onFileChosen = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await docsApi.upload(file);
      await casesApi.associate(caseId, uploaded.id);
      pushToast("success", `Uploaded ${file.name} (doc #${uploaded.id}); ingestion queued`);
      refresh();
      onChanged();
    } catch (err) {
      pushToast(
        "error",
        err instanceof ApiError ? `${err.code}: ${err.message}` : "Upload failed",
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onDisassociate = async (documentId: number): Promise<void> => {
    if (!window.confirm("Remove this document from the Case? (The document itself stays.)")) return;
    try {
      await casesApi.disassociate(caseId, documentId);
      pushToast("success", "Document unlinked");
      refresh();
      onChanged();
    } catch (err) {
      pushToast(
        "error",
        err instanceof ApiError ? err.message : "Failed to unlink document",
      );
    }
  };

  return (
    <section className="card p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-lg font-medium text-slate-800">Documents in this Case</h2>
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.tif,.tiff"
            className="hidden"
            onChange={(e) => onFileChosen(e.target.files?.[0])}
          />
          <button
            type="button"
            className="btn-primary"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? "Uploading…" : "Upload document"}
          </button>
          <button type="button" className="btn-secondary" onClick={refresh}>
            Refresh
          </button>
        </div>
      </div>
      <p className="mb-4 text-xs text-slate-500">
        Allowed: PDF, DOCX, TXT, PNG, JPG, TIFF. PII is redacted during ingestion; only the
        redacted text is searchable. Originals remain encrypted in the Vault under your
        per-tenant KMS key.
      </p>
      {loading && <p className="text-sm text-slate-500">Loading documents…</p>}
      {error && <p className="text-sm text-red-700">Could not load documents: {error.message}</p>}
      {data && data.documents.length === 0 && (
        <p className="text-sm text-slate-600">No documents in this Case yet.</p>
      )}
      {data && data.documents.length > 0 && (
        <ul role="list" className="divide-y divide-slate-200">
          {data.documents.map((d) => (
            <li key={d.document_id} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Link
                    to={`/documents/${d.document_id}`}
                    className="truncate font-medium text-slate-900 hover:text-brand-700"
                  >
                    {d.filename}
                  </Link>
                  <DocumentStatusBadge status={d.status} />
                  <CrossCheckableBadge cross_checkable={d.cross_checkable} />
                </div>
                <div className="text-xs text-slate-500">
                  {d.doc_type ?? "uncategorized"}
                  {d.tags.length > 0 && (
                    <span> · tags: {d.tags.map((t) => `#${t}`).join(" ")}</span>
                  )}
                  {d.other_case_ids.length > 0 && (
                    <span> · also in Cases #{d.other_case_ids.join(", #")}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/documents/${d.document_id}`} className="btn-secondary">
                  Open
                </Link>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => onDisassociate(d.document_id)}
                >
                  Unlink
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
